import { useContext, useState } from 'react';
import {
  Fab,
  Box,
  Zoom,
  useTheme,
  Card,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Menu,
  MenuItem
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { ChatbotAdmin } from './ChatbotAdmin';
import { ChatbotChat } from './ChatbotChat';
import { useChatbotContext } from 'src/contexts/ChatbotContext';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import RemoveIcon from '@mui/icons-material/Remove';

export const ChatbotPopup = ({
  setIsChatOpen,
  isChatOpen
}: {
  setIsChatOpen: (isOpen: boolean) => void;
  isChatOpen: boolean;
}) => {
  const theme = useTheme();
  const [isEnlarged, setIsEnlarged] = useState<boolean>(false);
  const isLargeViewport = useMediaQuery(theme.breakpoints.up('md'));
  const [isMinimised, setIsMinimised] = useState<boolean>();
  const [openMoreOptions, setOpenMoreOptions] = useState<boolean>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const showEnlarged = isEnlarged || !isLargeViewport;
  const [chatbotMode, setChatbotMode] = useState<'chat' | 'load' | 'admin'>('chat');
  const { memberid } = useContext(AuthContext) as AuthContextType;
  const [dialogOpen, setDialogOpen] = useState(false);
  const isAdmin = ['626287'].includes(memberid ?? 'null'); // TODO: change this to be Role based. 'staff'?

  const { startNewConversation } = useChatbotContext();

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const handleCloseClick = () => {
    setChatbotMode('chat');
    setIsChatOpen(false);
  };
  const handleResizeClick = () => {
    setIsEnlarged(!isEnlarged);
  };
  const handleMinimiseClick = () => {
    setIsMinimised(true);
  };
  const handleNewConversationClick = () => {
    setChatbotMode('chat');
    startNewConversation();
  };
  const handleAdminClick = () => {
    if (chatbotMode === 'admin') {
      setChatbotMode('chat');
    } else {
      setChatbotMode('admin');
    }
  };
  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleMoreOptionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenMoreOptions(true);
  };
  const handleMoreOptionsClose = () => {
    setAnchorEl(null);
    setOpenMoreOptions(false);
  };

  return (
    <>
      <Zoom
        in={isMinimised}
        timeout={transitionDuration}
        style={{
          transitionDelay: `${isMinimised ? transitionDuration.exit : 0}ms`
        }}
        unmountOnExit
      >
        <Fab
          variant="extended"
          size="large"
          aria-label={'Open chat bot'}
          color={'secondary'}
          sx={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            textTransform: 'initial'
          }}
          onClick={() => {
            setIsMinimised(false);
          }}
        >
          <MarkUnreadChatAltIcon sx={{ mr: 1 }} />
          Open CPD Assistant
        </Fab>
      </Zoom>
      <Zoom
        in={isChatOpen && !isMinimised}
        timeout={transitionDuration}
        style={{
          transitionProperty: 'height',
          transformOrigin: 'bottom right',
          transitionDelay: `${isChatOpen ? transitionDuration.exit : 0}ms`
        }}
        unmountOnExit
      >
        <Box
          sx={{
            position: 'fixed',
            bottom: '5px',
            right: '5px',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            [theme.breakpoints.up('md')]: {
              bottom: '30px',
              right: '30px'
            }
          }}
        >
          <Card
            elevation={5}
            sx={{
              height: isMinimised ? '0px' : 'calc(100vh - 5px - 5px)',
              width: 'calc(100vw - 5px - 20px)',
              transition:
                'width 200ms, height 200ms, max-height 200ms, transform 300ms cubic-bezier(0, 1.2, 1, 1), opacity 83ms ease-out',
              maxWidth: 'calc(100vw - 5px - 5px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'stretch',
              [theme.breakpoints.up('md')]: {
                height: showEnlarged ? 'calc(100vh - 60px)' : '600px',
                width: showEnlarged ? 'min(calc(100vw - 5px - 20px), 688px)' : '400px'
              },
              overflowY: 'hidden',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid #eee',
                gap: '8px',
                backgroundColor: '#11364d',
                color: '#fff',
                alignItems: 'center'
              }}
            >
              <Box sx={{ padding: '16px 0 16px 16px' }}>
                <Typography variant="body1" color="inherit">
                  RACGP CPD Virtual Assistant
                </Typography>
              </Box>
              <Box
                sx={{
                  marginLeft: 'auto',
                  minWidth: 'initial',
                  color: 'inherit',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  padding: '16px 8px 16px 16px'
                }}
              >
                <Box>
                  <Tooltip title="Minimise">
                    <IconButton color="inherit" aria-label="minimise" onClick={handleMinimiseClick}>
                      <RemoveIcon sx={{ color: 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More options">
                    <IconButton
                      color="inherit"
                      aria-label="options"
                      onClick={handleMoreOptionsOpen}
                      aria-controls={openMoreOptions ? 'account-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMoreOptions ? 'true' : undefined}
                    >
                      <MoreHorizIcon sx={{ color: 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open}
                    onClose={handleMoreOptionsClose}
                    onClick={handleMoreOptionsClose}
                    disableScrollLock
                    slotProps={{
                      paper: {
                        elevation: 0,
                        sx: {
                          overflow: 'visible',
                          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                          mt: 1.5,
                          '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1
                          },
                          '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0
                          },
                          '& .MuiIconButton-root:hover': {
                            borderRadius: '0',
                            backgroundColor: 'initial'
                          }
                        }
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem>
                      {isLargeViewport && (
                        <IconButton color="inherit" aria-label="close" onClick={handleResizeClick}>
                          <OpenInFullIcon
                            sx={{
                              color: 'inherit',
                              transform: 'scaleX(-1)',
                              height: '0.8em',
                              width: '0.8em',
                              mr: 1
                            }}
                          />
                          <Typography fontSize={'small'}>Enlarge or Shrink</Typography>
                        </IconButton>
                      )}
                    </MenuItem>
                    <MenuItem onClick={handleMoreOptionsClose}>
                      <IconButton
                        color="inherit"
                        aria-label="Start new conversation"
                        onClick={handleNewConversationClick}
                      >
                        <RestartAltIcon
                          sx={{ color: 'inherit', height: '.8em', width: '.8em', mr: 1 }}
                        />
                        <Typography fontSize={'small'}>New Coversation</Typography>
                      </IconButton>
                    </MenuItem>
                    <MenuItem onClick={handleMoreOptionsClose}>
                      {isAdmin && (
                        <IconButton
                          color="inherit"
                          aria-label="Download feedback"
                          onClick={handleAdminClick}
                        >
                          <SupervisorAccountIcon
                            sx={{ color: 'inherit', height: '.8em', width: '.8em', mr: 1 }}
                          />
                          <Typography fontSize={'small'}>Feedback</Typography>
                        </IconButton>
                      )}
                    </MenuItem>
                  </Menu>

                  <Tooltip title="Close">
                    <IconButton color="inherit" aria-label="close" onClick={handleDialogOpen}>
                      <CloseIcon sx={{ color: 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                padding: '16px',
                flex: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'stretch',
                overflowY: 'auto',
                marginBottom: '100px'
              }}
            >
              {chatbotMode === 'chat' && <ChatbotChat showEnlarged={showEnlarged} />}
              {chatbotMode === 'admin' && <ChatbotAdmin />}

              {chatbotMode === 'load' && <>Load!</>}
            </Box>
            <Dialog
              open={dialogOpen}
              onClose={handleDialogClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              disablePortal
              sx={{ position: 'absolute' }}
              disableScrollLock
            >
              <DialogTitle id="alert-dialog-title">
                {'Are you sure you want to end the conversation?'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Closing this chat will clear the conversation and it won’t be saved.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button color="inherit" onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button color="inherit" onClick={handleCloseClick} autoFocus>
                  End
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
        </Box>
      </Zoom>
    </>
  );
};

export default ChatbotPopup;
